import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ChevronDown, Lock, Search } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { DateInput } from '@/components/date-input';
import { Checkbox, ErrorBanner, Field, NativeSelect, Textarea } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { serverFieldErrors } from '@/components/settings-section';
import { VatTabs } from '@/components/vat';
import { toast } from '@/components/toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  RETURN_BOXES,
  TAX_KIND_LABELS,
  formatRate,
  taxCodeDescription,
  useTaxCodes,
  type TaxCode,
} from '@/lib/accounts';
import { ApiError, api } from '@/lib/api';
import { useMe } from '@/lib/clients';
import { todayIso } from '@/lib/dates';
import { formatDate } from '@/lib/format';
import { useOrgId } from '@/lib/org';
import { isIndia, useOrgLocale, useArabicEnabled } from '@/lib/org-locale';
import { ArabicField } from '@/components/arabic-field';
import { arabicValue } from '@/lib/arabic';
import { SPLIT_KINDS, ZERO_KINDS, taxRateFormErrors } from '@/lib/tax-rate-form';
import { i18n, useLanguage, useT } from '@/lib/i18n';
import { ltr, nameFor } from '@/lib/arabic';

export const Route = createFileRoute('/taxes/vat/rates')({ component: TaxRatesPage });

const APPLIES: Record<TaxCode['appliesTo'], string> = {
  sales: 'Sales',
  purchases: 'Purchases',
  both: 'Sales and purchases',
};

/** Labels in the reader's language, read at call time (the maps above keep their English). */
const appliesLabel = (k: TaxCode['appliesTo']): string =>
  ({
    sales: i18n.t('taxPages.vatRates.applies.sales'),
    purchases: i18n.t('taxPages.vatRates.applies.purchases'),
    both: i18n.t('taxPages.vatRates.applies.both'),
  })[k];

const kindLabel = (k: TaxCode['kind']): string =>
  ({
    standard: i18n.t('taxPages.vatRates.kind.standard'),
    zero: i18n.t('taxPages.vatRates.kind.zero'),
    exempt: i18n.t('taxPages.vatRates.kind.exempt'),
    out_of_scope: i18n.t('taxPages.vatRates.kind.outOfScope'),
    reverse_charge: i18n.t('taxPages.vatRates.kind.reverseCharge'),
  })[k];

const gstCategoryLabel = (k: string): string => {
  const m: Record<string, string> = {
    TAX: i18n.t('taxPages.vatRates.gstCategory.TAX'),
    EXP: i18n.t('taxPages.vatRates.gstCategory.EXP'),
    NIL: i18n.t('taxPages.vatRates.gstCategory.NIL'),
    EXEMPT: i18n.t('taxPages.vatRates.gstCategory.EXEMPT'),
    NONGST: i18n.t('taxPages.vatRates.gstCategory.NONGST'),
    RCM: i18n.t('taxPages.vatRates.gstCategory.RCM'),
  };
  return m[k] ?? k;
};

const returnBoxLabel = (b: { value: string; label: string }): string => {
  const m: Record<string, string> = {
    '1': i18n.t('taxPages.vatRates.box.b1'),
    '2': i18n.t('taxPages.vatRates.box.b2'),
    '3': i18n.t('taxPages.vatRates.box.b3'),
    '4': i18n.t('taxPages.vatRates.box.b4'),
    '5': i18n.t('taxPages.vatRates.box.b5'),
    '6': i18n.t('taxPages.vatRates.box.b6'),
    '7': i18n.t('taxPages.vatRates.box.b7'),
    '9': i18n.t('taxPages.vatRates.box.b9'),
    '10': i18n.t('taxPages.vatRates.box.b10'),
  };
  return m[b.value] ?? b.label;
};

/** Screen T3 (document 06): Taxes › VAT › Tax rates, list pattern with a drawer. */
function TaxRatesPage() {
  const india = useOrgLocale().taxPack === 'in';
  const t = useT();
  const lang = useLanguage();
  const orgId = useOrgId();
  const me = useMe();
  const canEdit = me.data?.isStaff ?? false;
  const [includeInactive, setIncludeInactive] = useState(false);
  const [q, setQ] = useState('');
  const [drawer, setDrawer] = useState<{ open: boolean; code: TaxCode | null }>({
    open: false,
    code: null,
  });
  const codes = useTaxCodes({ includeInactive });
  const qc = useQueryClient();
  const toggle = useMutation({
    mutationFn: (tc: TaxCode) =>
      api<TaxCode>(
        `/orgs/${orgId}/tax-codes/${tc.id}/${tc.isActive ? 'deactivate' : 'reactivate'}`,
        {
          method: 'POST',
        },
      ),
    onSuccess: async (tc) => {
      await qc.invalidateQueries({ queryKey: ['tax-codes', orgId] });
      toast(
        tc.isActive
          ? t('taxPages.vatRates.toastActive', { code: ltr(tc.code) })
          : t('taxPages.vatRates.toastInactive', { code: ltr(tc.code) }),
      );
    },
    onError: (e) => toast(e.message, { tone: 'error' }),
  });

  const needle = q.trim().toLowerCase();
  const rows = (codes.data ?? []).filter(
    (tc) => !needle || `${tc.code} ${tc.name} ${tc.nameAr ?? ''}`.toLowerCase().includes(needle),
  );

  return (
    <>
      <PageHeader
        title={india ? t('taxPages.vatRates.titleIndia') : t('taxPages.vatRates.titleUae')}
        description={
          india ? t('taxPages.vatRates.descriptionIndia') : t('taxPages.vatRates.descriptionUae')
        }
        actions={
          canEdit ? (
            <Button onClick={() => setDrawer({ open: true, code: null })}>
              {t('taxPages.vatRates.newTaxRate')}
            </Button>
          ) : null
        }
      />
      {india ? null : <VatTabs />}
      {!orgId ? (
        <EmptyState title={t('shell.selectClient')} hint={t('home.selectClientHint')} />
      ) : (
        <>
          <div className="mb-3 flex items-center gap-4">
            <div className="relative w-[320px]">
              <Search className="pointer-events-none absolute start-2 top-2.5 size-4 text-muted-foreground" />
              <Input
                className="bg-card ps-8"
                placeholder={t('taxPages.vatRates.filterPlaceholder')}
                aria-label={t('taxPages.vatRates.filterLabel')}
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            <Checkbox
              label={t('taxPages.vatRates.includeInactive')}
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
          </div>
          {codes.isPending ? (
            <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
          ) : codes.isError ? (
            <EmptyState title={t('taxPages.vatRates.loadError')} hint={codes.error.message} />
          ) : rows.length === 0 ? (
            <EmptyState title={t('taxPages.vatRates.noMatch')} />
          ) : (
            <div className="rounded-lg border bg-card">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[90px]">{t('taxPages.vatRates.col.code')}</TableHead>
                    <TableHead>{t('taxPages.vatRates.col.name')}</TableHead>
                    <TableHead className="w-[80px] text-end">
                      {t('taxPages.vatRates.col.rate')}
                    </TableHead>
                    <TableHead>{t('taxPages.vatRates.col.kind')}</TableHead>
                    <TableHead>{t('taxPages.vatRates.col.appliesTo')}</TableHead>
                    <TableHead className="w-[100px]">
                      {india
                        ? t('taxPages.vatRates.col.returnCategory')
                        : t('taxPages.vatRates.col.returnBox')}
                    </TableHead>
                    <TableHead className="w-[130px]">
                      {t('taxPages.vatRates.col.effectiveFrom')}
                    </TableHead>
                    <TableHead className="w-[90px]">{t('taxPages.vatRates.col.status')}</TableHead>
                    <TableHead className="w-[140px] text-end">
                      {t('taxPages.vatRates.col.action')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((tc) => (
                    <TableRow key={tc.id} className={tc.isActive ? '' : 'text-muted-foreground'}>
                      <TableCell className="font-medium">
                        <span className="inline-flex items-center gap-1.5">
                          {tc.code}
                          {tc.inUse ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Lock
                                  className="size-3.5 text-muted-foreground"
                                  aria-label={t('taxPages.vatRates.usedOnTransactions')}
                                />
                              </TooltipTrigger>
                              <TooltipContent>{t('taxPages.vatRates.usedTooltip')}</TooltipContent>
                            </Tooltip>
                          ) : null}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div>{nameFor(lang, tc.name, tc.nameAr)}</div>
                        {tc.description ? (
                          <div className="max-w-[380px] truncate text-xs text-muted-foreground">
                            {taxCodeDescription(tc, lang)}
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell className="tnum text-end">{formatRate(tc.rate)}</TableCell>
                      <TableCell>{kindLabel(tc.kind)}</TableCell>
                      <TableCell>{appliesLabel(tc.appliesTo)}</TableCell>
                      <TableCell>
                        {india
                          ? [
                              tc.returnBox
                                ? gstCategoryLabel(tc.returnBox)
                                : t('taxPages.vatRates.notReported'),
                              tc.gstSplit ? GST_SPLIT_LABELS[tc.gstSplit] : null,
                            ]
                              .filter(Boolean)
                              .join(' · ')
                          : tc.returnBox
                            ? tc.returnBox === '1'
                              ? '1a–1g'
                              : tc.returnBox
                            : '—'}
                      </TableCell>
                      <TableCell>{formatDate(tc.effectiveFrom)}</TableCell>
                      <TableCell>
                        {tc.isActive ? (
                          <Badge variant="outline">{t('taxPages.vatRates.active')}</Badge>
                        ) : (
                          <Badge variant="secondary">{t('common.inactive')}</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-end">
                        {canEdit ? (
                          <div className="inline-flex">
                            <Button
                              size="sm"
                              variant="link"
                              className="px-2"
                              onClick={() => setDrawer({ open: true, code: tc })}
                            >
                              {t('common.edit')}
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  size="icon-sm"
                                  variant="ghost"
                                  aria-label={t('taxPages.vatRates.moreActions', {
                                    code: ltr(tc.code),
                                  })}
                                >
                                  <ChevronDown className="size-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onSelect={() => toggle.mutate(tc)}>
                                  {tc.isActive
                                    ? t('taxPages.vatRates.makeInactive')
                                    : t('taxPages.vatRates.makeActive')}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <p className="mt-3 text-xs text-muted-foreground">
            {t('taxPages.vatRates.inactiveNote')}
          </p>
        </>
      )}
      <TaxRateDrawer
        open={drawer.open}
        code={drawer.code}
        onOpenChange={(open) => setDrawer((d) => ({ ...d, open }))}
      />
    </>
  );
}

const GST_CATEGORY_LABELS: Record<string, string> = {
  TAX: 'Taxable',
  EXP: 'Export (zero rated)',
  NIL: 'Nil rated',
  EXEMPT: 'Exempt',
  NONGST: 'Non-GST',
  RCM: 'Reverse charge',
};
const GST_SPLIT_LABELS: Record<string, string> = { cgst_sgst: 'CGST + SGST', igst: 'IGST' };

interface Form {
  code: string;
  name: string;
  nameAr: string;
  kind: TaxCode['kind'];
  rate: string;
  appliesTo: TaxCode['appliesTo'];
  returnBox: string;
  gstSplit: string;
  effectiveFrom: string;
  description: string;
}

const blank = (): Form => ({
  code: '',
  name: '',
  nameAr: '',
  kind: 'standard',
  rate: '5',
  appliesTo: 'both',
  returnBox: isIndia() ? 'TAX' : '1',
  gstSplit: isIndia() ? 'cgst_sgst' : '',
  effectiveFrom: todayIso(),
  description: '',
});

function TaxRateDrawer({
  open,
  code,
  onOpenChange,
}: {
  open: boolean;
  code: TaxCode | null;
  onOpenChange: (open: boolean) => void;
}) {
  const orgId = useOrgId();
  const t = useT();
  const qc = useQueryClient();
  const [form, setForm] = useState<Form>(blank());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const arabicOn = useArabicEnabled();
  const set =
    <K extends keyof Form>(k: K) =>
    (v: Form[K]) =>
      setForm((f) => ({ ...f, [k]: v }));
  useEffect(() => {
    if (!open) return;
    setErrors({});
    setForm(
      code
        ? {
            code: code.code,
            name: code.name,
            nameAr: code.nameAr ?? '',
            kind: code.kind,
            rate: code.rate,
            appliesTo: code.appliesTo,
            returnBox: code.returnBox ?? '',
            gstSplit: code.gstSplit ?? '',
            effectiveFrom: code.effectiveFrom,
            description: code.description ?? '',
          }
        : blank(),
    );
  }, [open, code]);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        code: form.code.trim(),
        name: form.name.trim(),
        ...(arabicOn ? { nameAr: arabicValue(form.nameAr) } : {}),
        kind: form.kind,
        rate: form.rate.trim(),
        appliesTo: form.appliesTo,
        returnBox: form.returnBox || null,
        ...(isIndia() ? { gstSplit: form.gstSplit || null } : {}),
        effectiveFrom: form.effectiveFrom,
        description: form.description,
      };
      return code
        ? api<TaxCode>(`/orgs/${orgId}/tax-codes/${code.id}`, {
            method: 'PATCH',
            body: JSON.stringify(body),
          })
        : api<TaxCode>(`/orgs/${orgId}/tax-codes`, { method: 'POST', body: JSON.stringify(body) });
    },
    onSuccess: async (saved) => {
      await qc.invalidateQueries({ queryKey: ['tax-codes', orgId] });
      toast(t('taxPages.vatRates.toastSaved', { code: ltr(saved.code) }));
      onOpenChange(false);
    },
    onError: (e) => setErrors(serverFieldErrors(e)),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    const next = taxRateFormErrors(form, isIndia());
    setErrors(next);
    if (!Object.keys(next).length) save.mutate();
  }

  const locked = Boolean(code?.inUse);
  const banner =
    save.error && !(save.error instanceof ApiError && Object.keys(errors).length)
      ? save.error.message
      : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[520px]">
        <form noValidate onSubmit={submit} className="flex h-full flex-col">
          <SheetHeader>
            <SheetTitle>
              {code
                ? t('taxPages.vatRates.editTitle', { code: ltr(code.code) })
                : t('taxPages.vatRates.newTaxRate')}
            </SheetTitle>
            <SheetDescription>
              {locked ? t('taxPages.vatRates.lockedHint') : t('taxPages.vatRates.drawerHint')}
            </SheetDescription>
          </SheetHeader>
          <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4">
            <ErrorBanner>{banner}</ErrorBanner>
            <div className="grid grid-cols-[140px_1fr] gap-3">
              <Field label={t('taxPages.vatRates.col.code')} error={errors['code']}>
                {(id) => (
                  <Input
                    id={id}
                    value={form.code}
                    autoFocus={!code}
                    aria-invalid={Boolean(errors['code'])}
                    onChange={(e) => set('code')(e.target.value.toUpperCase())}
                  />
                )}
              </Field>
              <Field label={t('taxPages.vatRates.col.name')} error={errors['name']}>
                {(id) => (
                  <Input
                    id={id}
                    value={form.name}
                    maxLength={100}
                    aria-invalid={Boolean(errors['name'])}
                    onChange={(e) => set('name')(e.target.value)}
                  />
                )}
              </Field>
            </div>
            {arabicOn ? (
              <ArabicField
                value={form.nameAr}
                error={errors['nameAr']}
                onChange={(v) => set('nameAr')(v)}
              />
            ) : null}
            <div className="grid grid-cols-[1fr_140px] gap-3">
              <Field label={t('taxPages.vatRates.col.kind')}>
                {(id) => (
                  <NativeSelect
                    id={id}
                    value={form.kind}
                    disabled={locked}
                    onChange={(e) => {
                      const kind = e.target.value as TaxCode['kind'];
                      setForm((f) => ({
                        ...f,
                        kind,
                        rate: ZERO_KINDS.has(kind) ? '0' : f.rate === '0' ? '5' : f.rate,
                        // nil, exempt and non-GST rates carry no CGST/SGST or IGST split
                        gstSplit: SPLIT_KINDS.has(kind) ? f.gstSplit : '',
                      }));
                    }}
                  >
                    {Object.keys(TAX_KIND_LABELS).map((k) => (
                      <option key={k} value={k}>
                        {kindLabel(k as TaxCode['kind'])}
                      </option>
                    ))}
                  </NativeSelect>
                )}
              </Field>
              <Field label={t('taxPages.vatRates.ratePercent')} error={errors['rate']}>
                {(id) => (
                  <Input
                    id={id}
                    value={form.rate}
                    inputMode="decimal"
                    className="tnum text-end"
                    disabled={locked}
                    aria-invalid={Boolean(errors['rate'])}
                    onChange={(e) => set('rate')(e.target.value)}
                  />
                )}
              </Field>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label={t('taxPages.vatRates.col.appliesTo')}>
                {(id) => (
                  <NativeSelect
                    id={id}
                    value={form.appliesTo}
                    onChange={(e) => set('appliesTo')(e.target.value as TaxCode['appliesTo'])}
                  >
                    {Object.keys(APPLIES).map((k) => (
                      <option key={k} value={k}>
                        {appliesLabel(k as TaxCode['appliesTo'])}
                      </option>
                    ))}
                  </NativeSelect>
                )}
              </Field>
              <Field
                label={t('taxPages.vatRates.col.effectiveFrom')}
                error={errors['effectiveFrom']}
              >
                {(id) => (
                  <DateInput
                    id={id}
                    value={form.effectiveFrom}
                    invalid={Boolean(errors['effectiveFrom'])}
                    onChange={set('effectiveFrom')}
                  />
                )}
              </Field>
            </div>
            {isIndia() ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field
                  label={t('taxPages.vatRates.col.returnCategory')}
                  hint={t('taxPages.vatRates.returnCategoryHint')}
                >
                  {(id) => (
                    <NativeSelect
                      id={id}
                      value={form.returnBox}
                      onChange={(e) => set('returnBox')(e.target.value)}
                    >
                      <option value="">{t('taxPages.vatRates.notReportedBlocked')}</option>
                      {Object.keys(GST_CATEGORY_LABELS).map((v) => (
                        <option key={v} value={v}>
                          {gstCategoryLabel(v)}
                        </option>
                      ))}
                    </NativeSelect>
                  )}
                </Field>
                <Field
                  label={t('taxPages.vatRates.taxSplit')}
                  hint={t('taxPages.vatRates.taxSplitHint')}
                  error={errors['gstSplit']}
                >
                  {(id) => (
                    <NativeSelect
                      id={id}
                      value={form.gstSplit}
                      onChange={(e) => set('gstSplit')(e.target.value)}
                    >
                      <option value="">{t('taxPages.vatRates.splitNone')}</option>
                      {Object.entries(GST_SPLIT_LABELS).map(([v, label]) => (
                        <option key={v} value={v}>
                          {label}
                        </option>
                      ))}
                    </NativeSelect>
                  )}
                </Field>
              </div>
            ) : (
              <Field label={t('taxPages.vatRates.vat201Box')}>
                {(id) => (
                  <NativeSelect
                    id={id}
                    value={form.returnBox}
                    onChange={(e) => set('returnBox')(e.target.value)}
                  >
                    <option value="">{t('taxPages.vatRates.notReported')}</option>
                    {RETURN_BOXES.map((b) => (
                      <option key={b.value} value={b.value}>
                        {returnBoxLabel(b)}
                      </option>
                    ))}
                  </NativeSelect>
                )}
              </Field>
            )}
            <Field label={t('taxPages.vatRates.description')} error={errors['description']}>
              {(id) => (
                <Textarea
                  id={id}
                  maxLength={500}
                  value={form.description}
                  onChange={(e) => set('description')(e.target.value)}
                />
              )}
            </Field>
          </div>
          <SheetFooter className="flex-row justify-end border-t">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? t('common.saving') : t('common.saveAndClose')}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
